"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.common = void 0;
const zod_1 = require("zod");
var common;
(function (common) {
    common.property = zod_1.z.object({ key: zod_1.z.string(), name: zod_1.z.string() });
    const timeIntervalYear = zod_1.z.literal('year');
    const timeIntervalQuarter = zod_1.z.literal('quarter');
    const timeIntervalMonth = zod_1.z.literal('month');
    const timeIntervalWeek = zod_1.z.literal('week');
    const timeIntervalDayOfWeek = zod_1.z.literal('day_of_week');
    const timeIntervalDay = zod_1.z.literal('day');
    const timeIntervalHour = zod_1.z.literal('hour');
    common.timeInterval = zod_1.z.union([
        timeIntervalYear,
        timeIntervalQuarter,
        timeIntervalMonth,
        timeIntervalWeek,
        timeIntervalDay,
        timeIntervalHour,
        timeIntervalDayOfWeek,
    ]);
    common.cohortTimeInterval = zod_1.z.union([timeIntervalYear, timeIntervalMonth, timeIntervalWeek, timeIntervalDay]);
    common.sorting = zod_1.z.object({
        key: zod_1.z.string(),
        direction: zod_1.z.literal('ASC').or(zod_1.z.literal('DESC')),
    });
    common.slice = zod_1.z.object({
        limit: zod_1.z.number().optional(),
        offset: zod_1.z.number().optional(),
        sort: common.sorting.array().optional(),
    });
    common.expressionValue = zod_1.z.object({
        expression: zod_1.z.string(),
        version: zod_1.z.string(),
    });
    common.aggregation = zod_1.z.discriminatedUnion('type', [
        zod_1.z.object({ type: zod_1.z.literal('count'), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('count_distinct'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('sum'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('avg'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('min'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('max'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('first'), key: zod_1.z.string(), sort: common.sorting.array(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('last'), key: zod_1.z.string(), sort: common.sorting.array(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('median'), key: zod_1.z.string(), property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('custom'), value: common.expressionValue, property: common.property }),
        zod_1.z.object({ type: zod_1.z.literal('metric'), metricId: zod_1.z.string(), property: common.property }),
    ]);
    common.filterValue = zod_1.z
        .string()
        .or(zod_1.z.number())
        .or(zod_1.z.date())
        .or(zod_1.z.boolean())
        .or(common.expressionValue)
        .or(zod_1.z.undefined());
    const filterConditionPartial = zod_1.z.object({
        key: zod_1.z.string(),
        value: common.filterValue,
    });
    common.filterCondition = zod_1.z.union([
        filterConditionPartial.extend({ operator: zod_1.z.literal('==') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('!=') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('<') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('>') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('<=') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('>=') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('icontains') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('noticontains') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('arrcontains') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('isnull') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('isnotnull') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('isempty') }),
        filterConditionPartial.extend({ operator: zod_1.z.literal('isnotempty') }),
    ]);
    common.grouping = zod_1.z.object({
        key: zod_1.z.string(),
        precision: zod_1.z.union([common.timeInterval, common.expressionValue]).optional(),
        fill: zod_1.z.boolean().optional(),
    });
    common.compositeFilterCondition = zod_1.z.discriminatedUnion('operator', [
        zod_1.z.object({
            operator: zod_1.z.literal('and'),
            operands: zod_1.z.lazy(() => common.compositeFilterCondition.array()),
        }),
        zod_1.z.object({
            operator: zod_1.z.literal('or'),
            operands: zod_1.z.lazy(() => common.compositeFilterCondition.array()),
        }),
        ...common.filterCondition.options,
    ]);
    common.aggregationType = zod_1.z.union([
        zod_1.z.literal('count'),
        zod_1.z.literal('count_distinct'),
        zod_1.z.literal('sum'),
        zod_1.z.literal('avg'),
        zod_1.z.literal('min'),
        zod_1.z.literal('max'),
        zod_1.z.literal('first'),
        zod_1.z.literal('last'),
        zod_1.z.literal('median'),
        zod_1.z.literal('custom'),
        zod_1.z.literal('metric'),
    ]);
    common.queryVariable = zod_1.z.discriminatedUnion('type', [
        zod_1.z.object({ type: zod_1.z.literal('String'), key: zod_1.z.string(), value: zod_1.z.string() }),
        zod_1.z.object({ type: zod_1.z.literal('Number'), key: zod_1.z.string(), value: zod_1.z.number() }),
        zod_1.z.object({ type: zod_1.z.literal('Boolean'), key: zod_1.z.string(), value: zod_1.z.boolean() }),
        zod_1.z.object({ type: zod_1.z.literal('Date'), key: zod_1.z.string(), value: zod_1.z.string() }),
    ]);
    common.queryVariables = zod_1.z.array(common.queryVariable);
})(common || (exports.common = common = {}));
//# sourceMappingURL=common.js.map